import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  ShieldCheck,
  Clock,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Mail,
  User,
  MessageSquare,
  Menu,
  X,
  Award,
  Truck,
  BadgeEuro,
  Home,
  Building2,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { DISTRICTS, districtPath } from "@/data/districts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "030 34762216";
const PHONE_TEL = "+493034762216";
const LOGO_SRC = "/brand/rodex-logo.svg";

/* ---------- NAVBAR ---------- */
const Navbar = ({ base = "" }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: `${base}#leistungen`, label: "Leistungen" },
    { href: `${base}#ablauf`, label: "Ablauf" },
    { href: `${base}#faq`, label: "FAQ" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <a href={base || "#top"} className="flex items-center" data-testid="brand-logo">
          <img
            src={LOGO_SRC}
            alt="Rodex Berlin — Logo"
            className="h-11 sm:h-12 w-auto block"
            data-testid="brand-logo-img"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`text-sm font-semibold tracking-wide transition-colors ${
                scrolled ? "text-charcoal hover:text-brand-orange" : "text-white/90 hover:text-white"
              }`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${PHONE_TEL}`}
            className={`flex items-center gap-2 text-sm font-semibold ${
              scrolled ? "text-charcoal" : "text-white"
            }`}
            data-testid="nav-phone-link"
          >
            <Phone className="w-4 h-4" />
            {PHONE_DISPLAY}
          </a>
          <Button
            className="bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full px-5 h-11 font-semibold shadow-md shadow-brand-orange/20"
            onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="nav-cta-button"
          >
            Kostenlose Beratung
          </Button>
        </div>

        <button
          className={`lg:hidden p-2 ${scrolled ? "text-charcoal" : "text-white"}`}
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menü"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-3" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block text-charcoal font-medium py-2"
            >
              {l.label}
            </a>
          ))}
          <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 text-charcoal font-semibold py-2">
            <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
          </a>
          <Button
            className="w-full bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full"
            onClick={() => {
              setOpen(false);
              document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Kostenlose Beratung
          </Button>
        </div>
      )}
    </header>
  );
};

/* ---------- HERO ---------- */
const Hero = () => (
  <section
    id="top"
    className="relative hero-radial text-white pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-44 lg:pb-32 overflow-hidden"
    data-testid="hero-section"
  >
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="absolute top-20 right-[-100px] w-[500px] h-[500px] rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
    <div className="absolute bottom-0 left-[-150px] w-[400px] h-[400px] rounded-full bg-brand-blue/10 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
      <div className="lg:col-span-7 fade-up">
        <Badge className="bg-brand-orange/15 text-brand-orange hover:bg-brand-orange/20 border border-brand-orange/30 rounded-full px-3 py-1.5 mb-6 text-xs sm:text-sm" data-testid="hero-badge">
          <span className="w-2 h-2 rounded-full bg-brand-orange mr-2 animate-pulse" />
          Reaktion innerhalb 24 Stunden — ganz Berlin
        </Badge>

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight mb-5 sm:mb-6">
          Berlins Spezialist für<br />
          <span className="text-brand-orange">Nagetierbekämpfung.</span>
          <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl mt-3 sm:mt-4 font-medium text-white/80">
            Ratten & Mäuse — dauerhaft raus.
          </span>
        </h1>

        <p className="text-sm sm:text-base lg:text-lg text-white/75 max-w-xl mb-7 sm:mb-8 leading-relaxed">
          Andere bekämpfen alles. Wir nur eins —
          <span className="text-white font-semibold"> und das richtig. </span>
          Rodex Berlin ist auf Ratten und Mäuse spezialisiert: Diagnose, Bekämpfung, Prävention und
          Gutachten — mit transparenten, schriftlichen Angeboten und fachgerechter Bekämpfung nach BfR-Leitlinien.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-8 sm:mb-10">
          <Button
            size="lg"
            className="bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-semibold shadow-lg shadow-brand-orange/30"
            onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-cta-primary"
          >
            Kostenlose Beratung anfordern
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <a href={`tel:${PHONE_TEL}`} data-testid="hero-cta-phone">
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-white/30 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-semibold backdrop-blur"
            >
              <Phone className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              {PHONE_DISPLAY}
            </Button>
          </a>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-6 pt-6 sm:pt-8 border-t border-white/10 max-w-xl">
          <div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">100 %</div>
            <div className="text-[10px] sm:text-xs text-white/60 mt-1 leading-snug">Fokus auf Nager</div>
          </div>
          <div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">Berlin-weit</div>
            <div className="text-[10px] sm:text-xs text-white/60 mt-1 leading-snug">schnell vor Ort</div>
          </div>
          <div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">&lt; 24 h</div>
            <div className="text-[10px] sm:text-xs text-white/60 mt-1 leading-snug">Reaktionszeit</div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
        <div className="relative">
          <div className="absolute -inset-4 bg-brand-orange/15 rounded-3xl blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 aspect-square sm:aspect-[4/5] bg-charcoal-deep">
            <img
              src="https://images.unsplash.com/photo-1722522856136-f7d9f16fe165?w=900&fit=cover&auto=format&q=80&v=5"
              alt="Ratte — Spezialist für Nagetierbekämpfung Berlin Rodex"
              className="w-full h-full object-cover object-center"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal-deep/70 via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- TRUST BAR ---------- */
const TrustBar = () => {
  const items = [
    { icon: Award, label: "IHK-zertifiziert" },
    { icon: ShieldCheck, label: "TÜV Rheinland" },
    { icon: Target, label: "100 % Nager-Fokus" },
    { icon: BadgeEuro, label: "Schriftliche Angebote" },
    { icon: Truck, label: "Diskrete Anreise" },
    { icon: Clock, label: "24-h-Reaktion" },
  ];
  return (
    <section className="bg-white py-8 border-b border-slate-100" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {items.map((it) => (
            <div key={it.label} className="flex items-center gap-2.5 text-charcoal">
              <div className="w-9 h-9 rounded-full bg-orange-soft flex items-center justify-center flex-shrink-0">
                <it.icon className="w-4 h-4 text-brand-orange" />
              </div>
              <span className="text-xs lg:text-sm font-semibold leading-tight">{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- SERVICES (expandable accordion with full SEO content) ---------- */
const Services = () => {
  const services = [
    {
      emoji: "🐀",
      title: "Rattenbekämpfung in Berlin",
      sub: "Wanderratten & Hausratten — BfR-konforme Bekämpfung nach Biozidverordnung.",
      content: (
        <>
          <p>
            Die <strong>Rattenbekämpfung in Berlin</strong> ist unsere Kernkompetenz. Wir bekämpfen
            sowohl die Wanderratte (<em>Rattus norvegicus</em>) — die in Berlin am häufigsten
            vorkommende Art, oft in Kanalisation, Hinterhöfen und Kellern — als auch die deutlich
            seltenere Hausratte (<em>Rattus rattus</em>), die vor allem in Dachstühlen und
            Lagerräumen anzutreffen ist.
          </p>
          <p>
            <strong>Wann sollten Sie uns rufen?</strong> Sobald Sie verdächtige Spuren bemerken:
            walnussgroße Kotpellets, Nagespuren an Holz oder Kabeln, ein muffig-stechender Geruch im
            Keller, Trippelgeräusche in Zwischendecken oder direkt eine Rattensichtung. Auch ein
            einzelnes Tier bedeutet meist: das Nest ist nicht weit, die Population wächst täglich.
          </p>
          <p>
            <strong>Unser Vorgehen:</strong> Inspektion des Befalls, Identifikation der Rattenart,
            Lokalisierung von Laufwegen und Nestern, anschließend gezielte Bekämpfung mit
            verriegelten Köderboxen nach BfR-Leitlinien und der aktuellen Biozidverordnung. Wir
            arbeiten ausschließlich mit in Deutschland zugelassenen Antikoagulanzien-Ködern, die in
            sabotagesicheren Stationen ausgebracht werden — sicher für Kinder, Haustiere und
            Wildtiere.
          </p>
          <p>
            Nach erfolgreicher Bekämpfung dokumentieren wir sämtliche <strong>Einstiegsquellen</strong>
            {" "}— meist Rohrdurchführungen, defekte Kellerfenster oder Mauerwerksrisse — und übergeben
            Ihnen eine schriftliche Analyse mit unseren Empfehlungen. Bauliche Maßnahmen führen wir
            selbst nicht durch.
          </p>
          <p className="font-medium text-charcoal">
            Einsatzgebiete: ganz Berlin — von Pankow über Mitte und Kreuzberg bis
            Neukölln, Spandau, Charlottenburg-Wilmersdorf und Steglitz-Zehlendorf.
          </p>
        </>
      ),
    },
    {
      emoji: "🐁",
      title: "Mäusebekämpfung in Berlin",
      sub: "Haus- und Feldmäuse in Wohnung, Keller, Lager — schnelle, saubere Beseitigung.",
      content: (
        <>
          <p>
            Die <strong>Mäusebekämpfung in Berlin</strong> wird oft unterschätzt — dabei vermehren
            sich Hausmäuse (<em>Mus musculus</em>) rasant: ein Weibchen kann pro Jahr 5 bis 10 Würfe
            mit jeweils 4 bis 12 Jungtieren bekommen. Aus einem einzelnen Tier werden in wenigen
            Monaten Dutzende. Mäuse verursachen nicht nur Sachschäden durch Nagen an Kabeln,
            Verpackungen und Dämmungen, sondern übertragen auch Krankheitserreger wie Salmonellen
            und Hantaviren.
          </p>
          <p>
            <strong>Typische Hinweise auf einen Mäusebefall:</strong> reiskorngroße schwarze
            Kotspuren entlang von Wänden, angeknabberte Lebensmittelverpackungen, ein süßlich-muffiger
            Geruch und vor allem das charakteristische nächtliche Rascheln in Zwischendecken,
            Hohlräumen oder hinter Möbeln.
          </p>
          <p>
            <strong>Unser Vorgehen:</strong> Wir setzen je nach Befallssituation eine Kombination aus
            Schlagfallen, Lebendfallen und geschlossenen Köderstationen ein. Gerade in
            Privathaushalten, Kindergärten und gastronomischen Betrieben arbeiten wir bevorzugt mit
            mechanischen Fallen, um auf Biozide zu verzichten, wenn das möglich ist.
          </p>
          <p>
            Wichtig: Bei Mäusen reicht oft schon eine 6 mm breite Lücke als Einschlupfquelle. Wir
            spüren diese Einstiegsstellen auf, dokumentieren sie und geben Ihnen eine schriftliche
            Empfehlung. Bauliche Maßnahmen führen wir selbst nicht durch.
          </p>
          <p className="font-medium text-charcoal">
            Mäusebekämpfung Berlin — auch für Hausverwaltungen, Restaurants und Bäckereien mit
            HACCP-Anforderungen.
          </p>
        </>
      ),
    },
    {
      emoji: "🔍",
      title: "Befallsdiagnose & Beratung",
      sub: "Kotspuren, Nagespuren, Laufwege — wir finden Ursache, Einstiegspunkte und Nester.",
      content: (
        <>
          <p>
            Bevor wir bekämpfen, schauen wir genau hin. Unsere <strong>Befallsdiagnose</strong> ist
            das Fundament jeder erfolgreichen Nagerbekämpfung — und gleichzeitig der häufigste Grund,
            warum unsere Behandlungen nachhaltig wirken: Wir wissen, mit was wir es zu tun haben.
          </p>
          <p>
            Bei einem Vor-Ort-Termin (kostenlos und unverbindlich in Berlin) prüfen wir:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Artbestimmung:</strong> Ratte oder Maus? Wander- oder Hausratte? Hausmaus oder Spitzmaus?</li>
            <li><strong>Befallsstärke:</strong> Einzeltier oder etablierte Population? Junge oder erwachsene Tiere?</li>
            <li><strong>Laufwege:</strong> Wo bewegen sich die Tiere? Welche Räume sind betroffen?</li>
            <li><strong>Einstiegsquellen:</strong> Wie kommen die Tiere ins Gebäude? Rohrdurchführungen, Lüftungsschächte, Mauerritzen?</li>
            <li><strong>Nahrungsquellen:</strong> Was zieht die Tiere an? Lagerung, Müllmanagement, Tierfutter?</li>
            <li><strong>Versteck- und Nestplätze:</strong> Wo halten sich die Tiere tagsüber auf?</li>
          </ul>
          <p>
            Daraus erstellen wir ein <strong>schriftliches Angebot</strong> mit klarer
            Methodik und Zeitplan. Sie entscheiden danach in Ruhe — kein Verkaufsdruck.
          </p>
          <p>
            Für Gewerbe und Hausverwaltungen erstellen wir auf Wunsch zusätzlich ein
            <strong> dokumentiertes Befallsgutachten</strong>, das auch gegenüber Behörden,
            Versicherungen oder bei Erbstreitigkeiten Bestand hat.
          </p>
        </>
      ),
    },
    {
      emoji: "🧱",
      title: "Prävention & Beratung",
      sub: "Einstiegsanalyse & Empfehlung — Nagerschutz-Beratung für Altbau, Hofkellern und Gastro.",
      content: (
        <>
          <p>
            <strong>Prävention ist günstiger als jede Bekämpfung.</strong> Wer schon einmal mit
            Ratten oder Mäusen zu tun hatte, weiß: Das Problem kommt fast immer wieder — solange die
            Einstiegsquellen offen sind. Berlin mit seinem Altbau-Bestand, kombinierten Hof- und
            Kellergebäuden und stark genutzter Kanalisation ist ein Paradies für Nagetiere.
          </p>
          <p>
            Im Rahmen unserer <strong>Präventions-Beratung</strong> identifizieren und dokumentieren
            wir typische Schwachstellen:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Rohr- und Kabeldurchführungen in Außenwänden und Kellern</li>
            <li>Spalten unter Kellertüren und an Garagentoren</li>
            <li>Lüftungsöffnungen und Kellerlichtschächte</li>
            <li>Defekte Kellerfenster und morsche Holzkonstruktionen</li>
            <li>Mauerwerksrisse und schadhafte Putzflächen</li>
            <li>Rückstausicherungen an Abwasserleitungen (Schutz vor Kanalratten)</li>
          </ul>
          <p>
            Zu jeder Schwachstelle erhalten Sie von uns eine <strong>schriftliche Analyse und
            Empfehlung</strong>. Wir analysieren und beraten. Bauliche Maßnahmen führen wir
            selbst nicht durch.
          </p>
          <p>
            Für Gewerbebetriebe und Hausverwaltungen erstellen wir ein
            <strong> Präventionskonzept inkl. Wartungsplan</strong>, das auch in HACCP- und
            QM-Systeme eingebunden werden kann.
          </p>
        </>
      ),
    },
    {
      emoji: "🛡️",
      title: "Dauerüberwachung & Monitoring",
      sub: "Wartungsverträge mit Monitoring-Stationen für Gewerbe, Hausverwaltungen & Lager.",
      content: (
        <>
          <p>
            <strong>Dauerüberwachung</strong> ist Pflicht für jeden lebensmittelverarbeitenden
            Betrieb in Berlin — und sinnvoll für jede Hausverwaltung, jedes größere Lager und jeden
            sensiblen Standort (Schulen, Kitas, Pflegeheime, Tierarztpraxen).
          </p>
          <p>
            Wir installieren <strong>Monitoring-Stationen</strong> an strategisch wichtigen Punkten:
            Eingänge, Müllbereiche, Lagerräume, Übergänge zu Außenflächen. Die Stationen sind
            sabotagesicher verriegelt, mit Standort-Nummern versehen und in einem digitalen
            Lageplan dokumentiert.
          </p>
          <p>
            Im <strong>regelmäßigen Wartungsturnus</strong> (monatlich, alle 6 Wochen oder
            quartalsweise — je nach Risikoklasse) prüfen wir jede Station, dokumentieren den
            Zustand und übergeben Ihnen einen schriftlichen Bericht. So erkennen wir
            <strong> Befall im Frühstadium</strong>, bevor sich eine Population etablieren kann.
          </p>
          <p>
            Vorteile für Sie:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Audit-sichere Dokumentation für HACCP, IFS, BRC, QS und Behördenkontrollen</li>
            <li>Frühwarnsystem statt teure Akutbekämpfung</li>
            <li>Imageschutz — kein &bdquo;Schädlingsbekämpfer vor der Tür&ldquo; zum offensichtlichen Anlass</li>
            <li>Planbare, transparente Jahreskosten</li>
          </ul>
        </>
      ),
    },
    {
      emoji: "📄",
      title: "Gutachten & HACCP-Dokumentation",
      sub: "Schriftliche Dokumentation, HACCP-Protokolle, Hygiene-Konzepte für Behörden & Versicherung.",
      content: (
        <>
          <p>
            Für lebensmittelverarbeitende Betriebe, Großküchen, Hotels, Krankenhäuser und Lager mit
            Lebensmittelbezug ist ein dokumentiertes <strong>HACCP-Schädlingsbekämpfungskonzept</strong>
            {" "}rechtlich vorgeschrieben (Verordnung (EG) Nr. 852/2004 und LMHV §3).
          </p>
          <p>
            Wir erstellen für Sie:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Bestandsaufnahme</strong> mit Lageplan aller Köder- und Monitoring-Stationen</li>
            <li><strong>Gefahrenanalyse</strong> nach HACCP-Prinzipien — wo besteht welches Schädlingsrisiko?</li>
            <li><strong>Maßnahmenplan</strong> für Prävention, Monitoring und akute Bekämpfung</li>
            <li><strong>Monatliche/quartalsweise Inspektionsberichte</strong> — vollständig audit-ready</li>
            <li><strong>Notfallprotokoll</strong> für akute Befallssituationen</li>
            <li><strong>Schulungs-Nachweise</strong> für Ihr Personal (auf Wunsch)</li>
          </ul>
          <p>
            Zusätzlich erstellen wir <strong>Befalls- und Sanierungsgutachten</strong> für
            Hausverwaltungen, Versicherungen, Gerichtsstreitigkeiten (z. B. zwischen Mieter und
            Vermieter) und bei Immobilienkäufen mit Verdacht auf Vorbefall.
          </p>
          <p>
            Die Dokumentation halten wir <strong>nach den Vorgaben von IFS Food, BRC, QS und
            HACCP</strong> bereit — Ihre Audits laufen damit reibungslos.
          </p>
        </>
      ),
    },
  ];

  return (
    <section id="leistungen" className="py-20 lg:py-24 bg-slate-50" data-testid="services-section">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-10 lg:mb-14">
          <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em] mb-3">
            Unsere Leistungen
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal leading-tight tracking-tight mb-4">
            Nur Nagetiere. <br className="hidden lg:block" />
            Aber das <span className="text-brand-orange">richtig</span>.
          </h2>
          <p className="text-slate-600 text-base lg:text-lg">
            Wir sind keine Allround-Kammerjäger. Wir haben uns bewusst auf Ratten und Mäuse spezialisiert —
            weil Spezialisierung nachhaltige Ergebnisse bringt. Klicken Sie auf eine Leistung für Details.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-3" data-testid="services-accordion">
          {services.map((s, i) => (
            <AccordionItem
              key={s.title}
              value={`service-${i}`}
              className="bg-white border border-slate-200 rounded-2xl px-5 sm:px-7 data-[state=open]:border-brand-orange/50 data-[state=open]:shadow-lg transition-all"
            >
              <AccordionTrigger
                className="hover:no-underline py-5 sm:py-6"
                data-testid={`service-trigger-${i}`}
              >
                <div className="flex items-center gap-4 text-left flex-1 pr-3">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-orange-soft flex items-center justify-center flex-shrink-0">
                    <span className="text-xl sm:text-2xl">{s.emoji}</span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-charcoal leading-snug">{s.title}</h3>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-snug">{s.sub}</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-7 pt-1 pl-0 sm:pl-[72px]" data-testid={`service-content-${i}`}>
                <div className="space-y-3.5 text-slate-700 text-sm sm:text-[15px] leading-relaxed">
                  {s.content}
                </div>
                <div className="mt-6">
                  <Button
                    onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
                    className="bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full h-11 px-5 font-semibold text-sm"
                    data-testid={`service-cta-${i}`}
                  >
                    Kostenlose Beratung anfordern <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-charcoal rounded-2xl p-6 sm:p-8 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-brand-orange flex items-center justify-center flex-shrink-0">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-bold text-base sm:text-lg mb-1">Privat oder Gewerbe — wir kennen die Unterschiede.</div>
              <div className="text-white/70 text-xs sm:text-sm flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1.5"><Home className="w-4 h-4" /> Wohnung, Altbau, Haus</span>
                <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" /> Gastro, Lager, Hausverwaltung</span>
              </div>
            </div>
          </div>
          <Button
            className="bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full h-12 px-6 font-semibold w-full sm:w-auto"
            onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="services-cta"
          >
            Angebot anfordern <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

/* ---------- WHY US ---------- */
const WhyUs = () => {
  const points = [
    {
      icon: Target,
      title: "100 % Fokus auf Nager",
      desc: "Keine Allround-Kammerjäger. Wir kennen Rattenarten, Nager-Verhalten und Einstiegsquellen in- und auswendig.",
    },
    {
      icon: Clock,
      title: "Reaktion innerhalb 24 Stunden",
      desc: "Berlin-weit. Sie schreiben uns heute — wir sind morgen vor Ort. Kein stundenlanges Warten auf den Rückruf.",
    },
    {
      icon: Truck,
      title: "Diskrete, unmarkierte Fahrzeuge",
      desc: "Niemand sieht, warum wir kommen. Ihre Nachbarn, Kunden oder Gäste erfahren nichts.",
    },
    {
      icon: BadgeEuro,
      title: "Transparente Preise",
      desc: "Vor jedem Auftrag erhalten Sie ein schriftliches Angebot — keine versteckten Kosten.",
    },
  ];
  return (
    <section className="py-24 bg-white" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="absolute -inset-6 bg-gradient-to-br from-brand-orange/20 to-brand-blue/10 rounded-3xl blur-3xl" />
          <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1569169373444-1533c948aee9?w=900&h=1125&fit=crop&crop=entropy&auto=format&q=80&v=2"
              alt="Maus — Nagetierbekämpfung Berlin Rodex"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em] mb-3">
            Warum Rodex Berlin
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal leading-tight tracking-tight mb-8">
            Spezialist statt Generalist. <br />
            <span className="text-brand-orange">Das macht den Unterschied.</span>
          </h2>

          <div className="space-y-6">
            {points.map((p) => (
              <div key={p.title} className="flex gap-4" data-testid={`why-us-item-${p.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-orange-soft flex items-center justify-center">
                  <p.icon className="w-6 h-6 text-brand-orange" />
                </div>
                <div>
                  <h3 className="font-bold text-charcoal text-lg mb-1">{p.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- PROCESS ---------- */
const Process = () => {
  const steps = [
    { num: "01", title: "Anruf oder Anfrage", desc: "Beschreiben Sie uns kurz, was Sie beobachtet haben — telefonisch oder per Formular." },
    { num: "02", title: "Diagnose vor Ort", desc: "Innerhalb 24 h bei Ihnen: Ursachenanalyse, Einstiegssuche, schriftliches Angebot — kostenlos." },
    { num: "03", title: "Gezielte Bekämpfung", desc: "Nach BfR-Richtlinien, mit geschlossenen Köderboxen und abgestimmten Verfahren." },
    { num: "04", title: "Nachkontrolle & Beratung", desc: "Wir prüfen nach, analysieren mögliche Einstiegsstellen und geben Ihnen schriftliche Empfehlungen." },
  ];
  return (
    <section id="ablauf" className="py-24 bg-charcoal text-white relative overflow-hidden" data-testid="process-section">
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-brand-orange/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em] mb-3">
            So läuft es ab
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold leading-tight tracking-tight">
            Vom Anruf bis zur Ruhe — <br />in vier Schritten.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div
              key={s.num}
              className="glass-dark rounded-2xl p-7 relative hover:border-brand-orange/40 transition-all"
              data-testid={`process-step-${i + 1}`}
            >
              <div className="text-5xl font-bold text-brand-orange/40 mb-3">{s.num}</div>
              <h3 className="font-bold text-xl mb-2">{s.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden lg:block absolute top-10 -right-4 w-6 h-6 text-brand-orange/40" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQ = () => {
  const faqs = [
    {
      q: "Wie schnell seid ihr da?",
      a: "Wir reagieren auf jede Anfrage innerhalb von 24 Stunden — in der Regel sind wir binnen eines Werktages bei Ihnen vor Ort. Bei akuten Gewerbefällen (Gastro, Hausverwaltung) priorisieren wir entsprechend.",
    },
    {
      q: "Warum nur Ratten und Mäuse?",
      a: "Weil Spezialisierung Qualität bringt. Nagetierbekämpfung ist anspruchsvoll: richtige Artbestimmung, BfR-konforme Köder, Einstiegsanalyse und nachhaltige Prävention. Allround-Kammerjäger können vieles — aber Nager richtig bekämpfen ist ein eigenes Handwerk.",
    },
    {
      q: "Was kostet die Beratung?",
      a: "Die Erstberatung am Telefon ist immer kostenlos. Eine Vor-Ort-Diagnose inkl. schriftlichem Angebot erstellen wir ebenfalls kostenlos — Sie entscheiden dann, ob wir behandeln.",
    },
    {
      q: "Sind die Mittel sicher für Kinder und Haustiere?",
      a: "Ja. Wir arbeiten ausschließlich mit geschlossenen, verriegelten Köderboxen (BfR-konform) und schlagfangsicheren Methoden. Alle eingesetzten Biozide sind in Deutschland zugelassen und werden entsprechend Biozidverordnung dokumentiert.",
    },
    {
      q: "Merken die Nachbarn oder Gäste, dass ihr kommt?",
      a: "Nein. Unsere Fahrzeuge sind unmarkiert, unsere Mitarbeiter tragen zivile Kleidung bis zum Einsatzort. Diskretion ist Teil unseres Versprechens — besonders wichtig für Gastro, Hotels und Hausverwaltungen.",
    },
    {
      q: "Was ist, wenn die Nager nach der Behandlung zurückkommen?",
      a: "Bei unserer Vorgehensweise — gezielte Bekämpfung plus Analyse der Einstiegsstellen mit schriftlichen Empfehlungen — passiert das extrem selten. Sollten dennoch Spuren wieder auftauchen, melden Sie sich; wir prüfen die Lage und bewerten gemeinsam die nächsten Schritte. Bei vereinbarten Wartungsverträgen sind Folgeeinsätze ohnehin Teil des Services.",
    },
    {
      q: "Arbeitet ihr mit Hausverwaltungen und Gastro?",
      a: "Ja, wir haben Rahmenverträge mit Berliner Hausverwaltungen, Restaurants, Hotels und Lagern. Auf Wunsch monatliche Monitoring-Stationen, HACCP-konforme Protokolle und Jahresberichte.",
    },
  ];
  return (
    <section id="faq" className="py-24 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em] mb-3">
            Häufige Fragen
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal leading-tight tracking-tight">
            Gut zu wissen.
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-slate-50 border border-slate-200 rounded-xl px-6 data-[state=open]:bg-white data-[state=open]:border-brand-orange/40 data-[state=open]:shadow-md transition-all"
            >
              <AccordionTrigger className="text-left text-charcoal font-semibold hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ---------- LEAD FORM ---------- */
const LeadForm = ({ district }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    pest_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const pestOptions = [
    "Ratten",
    "Mäuse",
    "Unbekannte Nagetiere",
    "Nagespuren / Kotspuren",
    "Prävention & Beratung",
    "Gewerbe / Dauerwartung",
    "Gutachten / HACCP",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.pest_type) {
      toast.error("Bitte Name, Telefon, E-Mail und Schädlingsart ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      toast.success("Anfrage gesendet! Wir melden uns innerhalb von 24 Stunden.");
      setForm({ name: "", phone: "", email: "", address: "", pest_type: "", message: "" });
    } catch (err) {
      toast.error("Ups, etwas ist schief gelaufen. Bitte rufen Sie uns direkt an.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-slate-50" data-testid="lead-form-section">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em] mb-3">
            Kontakt & Beratung
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal leading-tight tracking-tight mb-5">
            Innerhalb 24 Stunden <br />
            <span className="text-brand-orange">haben Sie eine Antwort.</span>
          </h2>
          <p className="text-slate-600 mb-8 leading-relaxed">
            Schreiben Sie uns kurz, was Sie beobachtet haben — wir melden uns binnen 24 Stunden zurück
            und vereinbaren einen Termin. Kostenlos, unverbindlich, diskret.
          </p>

          <div className="space-y-4">
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-brand-orange transition-colors group"
              data-testid="contact-phone-card"
            >
              <div className="w-12 h-12 rounded-full bg-brand-orange flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Direkt anrufen</div>
                <div className="font-bold text-charcoal text-lg">{PHONE_DISPLAY}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200">
              <div className="w-12 h-12 rounded-full bg-charcoal flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Einsatzgebiet</div>
                <div className="font-bold text-charcoal">Ganz Berlin</div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200">
              <div className="w-12 h-12 rounded-full bg-charcoal flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Reaktionszeit</div>
                <div className="font-bold text-charcoal">Innerhalb 24 Stunden</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl p-7 lg:p-10 shadow-xl border border-slate-100" data-testid="lead-form-card">
          {sent ? (
            <div className="text-center py-12" data-testid="lead-form-success">
              <div className="w-16 h-16 rounded-full bg-orange-soft flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-brand-orange" />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-3">Vielen Dank!</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Wir haben Ihre Anfrage erhalten und melden uns innerhalb von 24 Stunden zurück.
                Für dringende Fälle rufen Sie bitte direkt an.
              </p>
              <Button
                variant="outline"
                onClick={() => setSent(false)}
                className="rounded-full"
                data-testid="lead-form-reset"
              >
                Neue Anfrage
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-charcoal font-medium mb-1.5 block">Name*</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-10 h-11 rounded-lg"
                      placeholder="Max Mustermann"
                      data-testid="lead-input-name"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-charcoal font-medium mb-1.5 block">Telefon*</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="pl-10 h-11 rounded-lg"
                      placeholder="0171 1234567"
                      data-testid="lead-input-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-charcoal font-medium mb-1.5 block">E-Mail*</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 h-11 rounded-lg"
                      placeholder="max@beispiel.de"
                      data-testid="lead-input-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address" className="text-charcoal font-medium mb-1.5 block">Adresse</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="pl-10 h-11 rounded-lg"
                      placeholder={district ? `Musterstraße 12, Berlin-${district}` : "Musterstraße 12, 10115 Berlin"}
                      data-testid="lead-input-address"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="pest_type" className="text-charcoal font-medium mb-1.5 block">Art der Anfrage*</Label>
                <Select
                  value={form.pest_type}
                  onValueChange={(v) => setForm({ ...form, pest_type: v })}
                >
                  <SelectTrigger id="pest_type" className="h-11 rounded-lg" data-testid="lead-select-pest">
                    <SelectValue placeholder="Bitte auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pestOptions.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`lead-pest-option-${p}`}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-charcoal font-medium mb-1.5 block">Kurze Beschreibung</Label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="pl-10 rounded-lg min-h-[100px]"
                    placeholder="Wo genau? Seit wann? Spuren, Geräusche, Sichtungen?"
                    data-testid="lead-input-message"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full font-semibold text-base shadow-lg shadow-brand-orange/30"
                data-testid="lead-submit-button"
              >
                {loading ? "Wird gesendet..." : "Kostenloses Angebot anfordern"}
                {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
              </Button>

              <p className="text-xs text-slate-500 text-center leading-relaxed">
                Mit dem Absenden stimmen Sie unserer <a href="/datenschutz" className="text-brand-orange underline">Datenschutzerklärung</a> zu.
                Keine Werbung, keine Weitergabe — nur Rückruf.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- FINAL CTA ---------- */
const FinalCTA = () => (
  <section className="bg-charcoal-deep relative overflow-hidden" data-testid="final-cta">
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/10 blur-3xl rounded-full pointer-events-none" />
    <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-20 text-center text-white">
      <h2 className="text-3xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
        Je länger Sie warten, <br />
        <span className="text-brand-orange">desto größer die Population.</span>
      </h2>
      <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
        Rufen Sie jetzt an oder schicken Sie uns eine Nachricht. Innerhalb 24 Stunden haben Sie eine Antwort.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a href={`tel:${PHONE_TEL}`}>
          <Button
            size="lg"
            className="bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full h-14 px-8 font-semibold shadow-xl shadow-brand-orange/40"
            data-testid="final-cta-call"
          >
            <Phone className="mr-2 w-5 h-5" />
            Jetzt anrufen: {PHONE_DISPLAY}
          </Button>
        </a>
        <Button
          size="lg"
          variant="outline"
          className="border-white/30 bg-white/5 hover:bg-white/10 text-white rounded-full h-14 px-8 font-semibold"
          onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
          data-testid="final-cta-form"
        >
          Formular ausfüllen
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </div>
  </section>
);

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer className="bg-charcoal-deep text-white/70 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <img src={LOGO_SRC} alt="Rodex Berlin Logo" className="h-12 w-auto mb-4 block" data-testid="footer-logo" />
        <p className="text-sm max-w-md leading-relaxed mb-4">
          Rodex Berlin ist Ihr zertifizierter Spezialist für Nagetierbekämpfung in Berlin.
          Ratten und Mäuse — unser einziger Fokus.
        </p>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="px-2.5 py-1 rounded-full border border-white/15">IHK-zertifiziert</span>
          <span className="px-2.5 py-1 rounded-full border border-white/15">TÜV Rheinland</span>
          <span className="px-2.5 py-1 rounded-full border border-white/15">HACCP-konform</span>
          <span className="px-2.5 py-1 rounded-full border border-white/15">BfR-Leitlinien</span>
        </div>
      </div>
      <div>
        <h4 className="text-white font-semibold mb-4">Kontakt</h4>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-brand-orange" />
            <a href={`tel:${PHONE_TEL}`} className="hover:text-white">{PHONE_DISPLAY}</a>
          </li>
          <li className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-brand-orange" />
            <a href="mailto:info@rodex-berlin.de" className="hover:text-white">info@rodex-berlin.de</a>
          </li>
          <li className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brand-orange" />
            Berlin
          </li>
        </ul>
      </div>
      <div>
        <h4 className="text-white font-semibold mb-4">Rechtliches</h4>
        <ul className="space-y-2 text-sm">
          <li><a href="/impressum" className="hover:text-white transition-colors">Impressum</a></li>
          <li><a href="/datenschutz" className="hover:text-white transition-colors">Datenschutz</a></li>
          <li><a href="/agb" className="hover:text-white transition-colors">AGB</a></li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-8 text-xs leading-relaxed" data-testid="footer-districts">
      <span className="text-white/90 font-semibold mr-2">Einsatzgebiete in Berlin:</span>
      {DISTRICTS.map((d, i) => (
        <React.Fragment key={d.slug}>
          {i > 0 && <span className="text-white/25 mx-1.5">·</span>}
          <Link
            to={districtPath(d.slug)}
            className="hover:text-white transition-colors whitespace-nowrap"
            data-testid={`footer-district-link-${d.slug}`}
          >
            {d.name}
          </Link>
        </React.Fragment>
      ))}
    </div>
    <div className="border-t border-white/5 py-5 text-center text-xs text-white/40">
      © 2025 Rodex Berlin — Alle Rechte vorbehalten.
    </div>
  </footer>
);

/* ---------- STICKY MOBILE CALL ---------- */
const StickyCall = () => (
  <a
    href={`tel:${PHONE_TEL}`}
    className="lg:hidden fixed bottom-4 right-4 z-40 bg-brand-orange text-white rounded-full h-14 w-14 flex items-center justify-center shadow-2xl shadow-brand-orange/40 pulse-ring"
    data-testid="sticky-call-button"
    aria-label="Anrufen"
  >
    <Phone className="w-6 h-6" />
  </a>
);

/* ---------- MAIN ---------- */
export default function LandingPage() {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView(), 50);
  }, []);
  return (
    <div className="bg-background min-h-screen" data-testid="landing-page">
      <Navbar />
      <main>
        <Hero />
        <TrustBar />
        <Services />
        <WhyUs />
        <Process />
        <FAQ />
        <LeadForm />
        <FinalCTA />
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}

export { Navbar, TrustBar, Process, LeadForm, FinalCTA, Footer, StickyCall, PHONE_DISPLAY, PHONE_TEL };
